import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Divider,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store/types';
import { fetchMaterialsByGst, fetchCustomers, fetchCompanies } from '@/store';
import { InvoiceData } from '../../types/invoice';
import { InvoicePDFGenerator } from '../../utils/invoicePDFGenerator';
import { numberToWords } from '../../utils/numberToWords';

interface EditableInvoiceFormProps {
  initialData?: Partial<InvoiceData>;
  purchaseOrder?: any;
  comprehensiveData?: any;
  onSave?: (data: InvoiceData) => void;
  onCancel?: () => void;
}

const validationSchema = Yup.object({
  invoiceNo: Yup.string().required('Invoice number is required'),
  invoiceDate: Yup.string().required('Invoice date is required'),
  companyName: Yup.string().required('Company name is required'),
  companyGstin: Yup.string().required('GSTIN is required'),
  items: Yup.array().of(
    Yup.object({
      description: Yup.string().required('Description is required'),
      hsn: Yup.string().required('HSN code is required'),
      qty: Yup.number().min(0, 'Quantity must be positive').required('Quantity is required'),
      rate: Yup.number().min(0, 'Rate must be positive').required('Rate is required'),
    })
  ).min(1, 'At least one item is required'),
});

export const EditableInvoiceForm: React.FC<EditableInvoiceFormProps> = ({
  initialData,
  purchaseOrder,
  comprehensiveData,
  onSave,
  onCancel,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sameAsBillTo, setSameAsBillTo] = useState(true); // Default to true for convenience
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch<AppDispatch>();

  // Get companies, customers, and materials from Redux store
  const { companies } = useSelector((state: RootState) => state.company);
  const { customers } = useSelector((state: RootState) => state.customer);
  const { materials } = useSelector((state: RootState) => state.material);

  // Load companies and customers on mount
  useEffect(() => {
    dispatch(fetchCompanies({ limit: 100 }));
    dispatch(fetchCustomers({ limit: 100 }));
  }, [dispatch]);

  // Load materials by GST when component mounts or PO changes
  useEffect(() => {
    if (purchaseOrder?.customerId?.gst && purchaseOrder?.companyId?._id) {
      dispatch(fetchMaterialsByGst({
        gst: purchaseOrder.companyId.gst,
        companyId: purchaseOrder.companyId._id,
        page: 1,
        limit: 1000,
        isActive: true,
        jobType: purchaseOrder.jobType,
      }));
    }
  }, [purchaseOrder, dispatch]);

  // Prepare description options from materials
  const descriptionOptions = useMemo(() => {
    if (!materials || materials.length === 0) return [];

    return materials.map((material: any) => {
      let description = '';
      if (material.materialName && material.materialType) {
        description = `${material.materialName} - ${material.materialType}`;
        if (material.description) {
          description += ` (${material.description})`;
        }
      } else if (material.materialName) {
        description = material.materialName;
      }
      return description.trim();
    }).filter(Boolean);
  }, [materials]);

  const getInitialValues = (): InvoiceData => {
    if (initialData) {
      return initialData as InvoiceData;
    }

    // Get company details from PO or use first company
    const company = companies.find(c => c._id === (purchaseOrder?.companyId?._id || purchaseOrder?.companyId)) || companies[0];

    // Get customer details from PO
    const customer = purchaseOrder?.customerId;

    // Prepare items from comprehensive data
    let items: any[] = [];

    if (comprehensiveData?.materialsSummary?.totalMaterialsUsed) {
      items = comprehensiveData.materialsSummary.totalMaterialsUsed.map((material: any, index: number) => {
        // Try to find the full material details from the fetched materials list
        const fullMaterial = materials?.find(m => m._id === material.materialId);

        // Build a comprehensive description
        let description = '';
        if (fullMaterial) {
          // Use full material details if available
          description = `${fullMaterial.materialName || ''} - ${fullMaterial.materialType || ''}`;
          // Add additional description if available
          if (fullMaterial.description) {
            description += ` (${fullMaterial.description})`;
          }
        } else {
          // Fallback to basic info from comprehensive data
          description = `${material.materialName} - ${material.materialType}`;
        }

        return {
          sno: index + 1,
          materialId: material.materialId || undefined, // Material ID reference
          companyId: purchaseOrder?.companyId?._id || purchaseOrder?.companyId || undefined, // Company ID for the material
          description: description.trim(),
          hsn: '', // HSN code must be entered manually (not available in Material model)
          qty: material.totalQuantityKg || material.totalQuantityCount,
          unit: material.totalQuantityKg > 0 ? 'KG' : 'NOS',
          rate: 0,
          cgstPercent: 9,
          cgstAmount: 0,
          sgstPercent: 9,
          sgstAmount: 0,
          amount: 0,
        };
      });
    }

    // If no materials found, add a default empty item
    if (items.length === 0) {
      items = [
        {
          sno: 1,
          description: '',
          hsn: '',
          qty: 1,
          unit: 'NOS',
          rate: 0,
          cgstPercent: 9,
          cgstAmount: 0,
          sgstPercent: 9,
          sgstAmount: 0,
          amount: 0,
        },
      ];
    }

    return {
      // Database IDs - Extract from purchase order
      customerId: purchaseOrder?.customerId?._id || purchaseOrder?.customerId || undefined,
      companyId: purchaseOrder?.companyId?._id || purchaseOrder?.companyId || company?._id || undefined,
      purchaseOrderId: purchaseOrder?._id || undefined,
      comprehensiveDataId: comprehensiveData?._id || undefined,

      companyName: company?.name || 'S&S ENGINEERING',
      companyAddress: company?.address1 || 'NO 84, TEMPLE TOWN',
      companyCity: company?.city || 'KUNDRATHUR',
      companyState: company?.state || 'Tamil Nadu',
      companyPincode: company?.pincode || '600069',
      companyCountry: 'India',
      companyGstin: company?.gst || '33BWFPS3172R1ZV',

      invoiceNo: purchaseOrder?.poNumber || '',
      invoiceDate: moment().format('DD/MM/YYYY'),
      terms: 'IMMEDIATE',
      dueDate: moment().format('DD/MM/YYYY'),
      placeOfSupply: 'Tamil Nadu (33)',

      billTo: {
        name: customer?.companyName || '',
        address: customer?.addressLine1 || '',
        city: customer?.city || '',
        state: customer?.state || '',
        pincode: customer?.pinCode || '',
        country: 'India',
        gstin: customer?.gst || '',
      },

      shipTo: {
        name: customer?.companyName || '',
        address: customer?.addressLine1 || '',
        city: customer?.city || '',
        state: customer?.state || '',
        pincode: customer?.pinCode || '',
        country: 'India',
      },

      items,

      subTotal: 0,
      cgstTotal: 0,
      sgstTotal: 0,
      grandTotal: 0,
      totalInWords: 'Rupees Zero Only',

      bankDetails: {
           bankName:  'STATE BANK OF INDIA',
        accountNumber:  '41222172304',
        ifscCode: 'SBIN0012931',
        branch:  'THIRUMUDIVAKKAM',
        // bankName: company?.bankName || 'STATE BANK OF INDIA',
        // accountNumber: company?.accountNumber || '41222172304',
        // ifscCode: company?.ifscCode || 'SBIN0012931',
        // branch: company?.branch || 'THIRUMUDIVAKKAM',
      },

      notes: 'Thanking you for your business',
      termsAndConditions:
        'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct',
    };
  };

  const calculateItemTotals = (item: any) => {
    const amount = item.qty * item.rate;
    const cgstAmount = (amount * item.cgstPercent) / 100;
    const sgstAmount = (amount * item.sgstPercent) / 100;
    return { amount, cgstAmount, sgstAmount };
  };

  const calculateTotals = (items: any[]) => {
    const subTotal = items.reduce((sum, item) => sum + item.amount, 0);
    const cgstTotal = items.reduce((sum, item) => sum + item.cgstAmount, 0);
    const sgstTotal = items.reduce((sum, item) => sum + item.sgstAmount, 0);
    const grandTotal = subTotal + cgstTotal + sgstTotal;
    const totalInWords = numberToWords(grandTotal);

    return { subTotal, cgstTotal, sgstTotal, grandTotal, totalInWords };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      console.log('=== Invoice Save Triggered ===');
      console.log('Database IDs:', {
        customerId: values.customerId,
        companyId: values.companyId,
        purchaseOrderId: values.purchaseOrderId,
        comprehensiveDataId: values.comprehensiveDataId,
      });
      console.log('Invoice Items with IDs:', values.items.map(item => ({
        materialId: item.materialId,
        companyId: item.companyId,
        description: item.description,
        hsn: item.hsn,
        qty: item.qty,
        unit: item.unit,
        rate: item.rate,
      })));
      console.log('Full Invoice Data:', values);
      console.log('Invoice Data JSON:', JSON.stringify(values, null, 2));
      console.log('onSave callback exists:', !!onSave);

      if (onSave) {
        onSave(values);
        console.log('onSave callback executed');
      } else {
        console.log('No onSave callback provided - invoice data logged to console');
        alert('Invoice data has been logged to the browser console. Open DevTools (F12) to view it.');
      }
    },
  });

  // Handle form submission with validation error display
  const handleSaveClick = async () => {
    console.log('Save button clicked');
    const errors = await formik.validateForm();
    console.log('Validation errors:', errors);

    if (Object.keys(errors).length > 0) {
      formik.setTouched({
        invoiceNo: true,
        invoiceDate: true,
        companyName: true,
        companyGstin: true,
        items: formik.values.items.map(() => ({
          description: true,
          hsn: true,
          qty: true,
          rate: true,
        })),
      });

      // Show error message
      const errorMessages: string[] = [];
      if (errors.invoiceNo) errorMessages.push('Invoice Number');
      if (errors.invoiceDate) errorMessages.push('Invoice Date');
      if (errors.companyName) errorMessages.push('Company Name');
      if (errors.companyGstin) errorMessages.push('GSTIN');
      if (errors.items) errorMessages.push('Item details (Description, HSN, Qty, Rate)');

      setErrorMessage(`Please fill in required fields: ${errorMessages.join(', ')}`);
      setErrorSnackbar(true);
      console.log('Validation failed:', errorMessages);
      return;
    }

    formik.handleSubmit();
  };

  // Sync Ship To with Bill To when checkbox is checked and Bill To changes
  useEffect(() => {
    if (sameAsBillTo) {
      formik.setFieldValue('shipTo.name', formik.values.billTo.name);
      formik.setFieldValue('shipTo.address', formik.values.billTo.address);
      formik.setFieldValue('shipTo.city', formik.values.billTo.city);
      formik.setFieldValue('shipTo.state', formik.values.billTo.state);
      formik.setFieldValue('shipTo.pincode', formik.values.billTo.pincode);
      formik.setFieldValue('shipTo.country', formik.values.billTo.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sameAsBillTo,
    formik.values.billTo.name,
    formik.values.billTo.address,
    formik.values.billTo.city,
    formik.values.billTo.state,
    formik.values.billTo.pincode,
    formik.values.billTo.country,
  ]);

  // Update item calculations when qty, rate, or tax percentages change
  useEffect(() => {
    const updatedItems = formik.values.items.map((item, index) => {
      const totals = calculateItemTotals(item);
      return { ...item, sno: index + 1, ...totals };
    });

    const totals = calculateTotals(updatedItems);

    formik.setValues({
      ...formik.values,
      items: updatedItems,
      ...totals,
    });
  }, [
    formik.values.items.map(i => `${i.qty}-${i.rate}-${i.cgstPercent}-${i.sgstPercent}`).join(','),
  ]);

  const handlePreview = () => {
    const generator = new InvoicePDFGenerator(formik.values);
    generator.generate();
    const dataUrl = generator.preview();
    setPdfUrl(dataUrl);
    setPreviewOpen(true);
  };

  const handleDownload = () => {
    const generator = new InvoicePDFGenerator(formik.values);
    generator.generate();
    const filename = `${formik.values.invoiceNo.replace(/\//g, '-')}.pdf`;
    generator.download(filename);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ p: 3 }}>
          {/* Header Section */}
          <Typography variant="h5" gutterBottom>
            Invoice Details
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Company Information */}
          <Typography variant="h6" gutterBottom>
            Company Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 , mt:2}}>
            {/* Company Selector */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                fullWidth
                options={companies || []}
                getOptionLabel={(option: any) => option.name || ''}
                value={companies?.find((c: any) => c._id === formik.values.companyId) || null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    // Update company ID
                    formik.setFieldValue('companyId', newValue._id);
                    // Update company details
                    formik.setFieldValue('companyName', newValue.name);
                    formik.setFieldValue('companyAddress', newValue.address1 || '');
                    formik.setFieldValue('companyCity', newValue.city || '');
                    formik.setFieldValue('companyState', newValue.state || '');
                    formik.setFieldValue('companyPincode', newValue.pincode || '');
                    formik.setFieldValue('companyGstin', newValue.gst || '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Company (Optional)"
                    placeholder="Choose a company or enter details manually"
                    helperText="Select a company to auto-fill details below"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                helperText={formik.touched.companyName && formik.errors.companyName}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="GSTIN"
                name="companyGstin"
                value={formik.values.companyGstin}
                onChange={formik.handleChange}
                error={formik.touched.companyGstin && Boolean(formik.errors.companyGstin)}
                helperText={formik.touched.companyGstin && formik.errors.companyGstin}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Address"
                name="companyAddress"
                value={formik.values.companyAddress}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="City"
                name="companyCity"
                value={formik.values.companyCity}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>

          {/* Invoice Details */}
          <Typography variant="h6" gutterBottom>
            Invoice Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3, mt:2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Invoice Number"
                name="invoiceNo"
                value={formik.values.invoiceNo}
                onChange={formik.handleChange}
                error={formik.touched.invoiceNo && Boolean(formik.errors.invoiceNo)}
                helperText={formik.touched.invoiceNo && formik.errors.invoiceNo}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Invoice Date"
                name="invoiceDate"
                value={formik.values.invoiceDate}
                onChange={formik.handleChange}
                error={formik.touched.invoiceDate && Boolean(formik.errors.invoiceDate)}
                helperText={formik.touched.invoiceDate && formik.errors.invoiceDate}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Terms"
                name="terms"
                value={formik.values.terms}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Due Date"
                name="dueDate"
                value={formik.values.dueDate}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>

          {/* Bill To & Ship To */}
          <Typography variant="h6" gutterBottom>
            Billing & Shipping Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3, mt:2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Bill To
                  </Typography>
                  {/* Customer Selector */}
                  <Autocomplete
                    fullWidth
                    options={customers || []}
                    getOptionLabel={(option: any) => option.companyName || ''}
                    value={customers?.find((c: any) => c._id === formik.values.customerId) || null}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        // Update customer ID
                        formik.setFieldValue('customerId', newValue._id);
                        // Update bill to details
                        formik.setFieldValue('billTo.name', newValue.companyName || '');
                        formik.setFieldValue('billTo.address', newValue.addressLine1 || '');
                        formik.setFieldValue('billTo.city', newValue.city || '');
                        formik.setFieldValue('billTo.state', newValue.state || '');
                        formik.setFieldValue('billTo.pincode', newValue.pinCode || '');
                        formik.setFieldValue('billTo.gstin', newValue.gst || '');
                        // If "Same as Bill To" is checked, update ship to as well
                        if (sameAsBillTo) {
                          formik.setFieldValue('shipTo.name', newValue.companyName || '');
                          formik.setFieldValue('shipTo.address', newValue.addressLine1 || '');
                          formik.setFieldValue('shipTo.city', newValue.city || '');
                          formik.setFieldValue('shipTo.state', newValue.state || '');
                          formik.setFieldValue('shipTo.pincode', newValue.pinCode || '');
                          formik.setFieldValue('shipTo.country', 'India');
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Customer (Optional)"
                        placeholder="Choose a customer"
                        size="small"
                        margin="dense"
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="billTo.name"
                    value={formik.values.billTo.name}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    name="billTo.address"
                    value={formik.values.billTo.address}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="City"
                    name="billTo.city"
                    value={formik.values.billTo.city}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="State"
                    name="billTo.state"
                    value={formik.values.billTo.state}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="billTo.pincode"
                    value={formik.values.billTo.pincode}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label="GSTIN"
                    name="billTo.gstin"
                    value={formik.values.billTo.gstin}
                    onChange={formik.handleChange}
                    margin="dense"
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Ship To
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sameAsBillTo}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSameAsBillTo(checked);
                            if (checked) {
                              // Copy Bill To data to Ship To
                              formik.setFieldValue('shipTo.name', formik.values.billTo.name);
                              formik.setFieldValue('shipTo.address', formik.values.billTo.address);
                              formik.setFieldValue('shipTo.city', formik.values.billTo.city);
                              formik.setFieldValue('shipTo.state', formik.values.billTo.state);
                              formik.setFieldValue('shipTo.pincode', formik.values.billTo.pincode);
                              formik.setFieldValue('shipTo.country', formik.values.billTo.country);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Same as Bill To</Typography>}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="shipTo.name"
                    value={formik.values.shipTo.name}
                    onChange={(e) => {
                      formik.handleChange(e);
                      // Uncheck if user manually edits
                      if (sameAsBillTo) setSameAsBillTo(false);
                    }}
                    margin="dense"
                    size="small"
                    disabled={sameAsBillTo}
                  />
                  <TextField
                    fullWidth
                    label="Address"
                    name="shipTo.address"
                    value={formik.values.shipTo.address}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (sameAsBillTo) setSameAsBillTo(false);
                    }}
                    margin="dense"
                    size="small"
                    disabled={sameAsBillTo}
                  />
                  <TextField
                    fullWidth
                    label="City"
                    name="shipTo.city"
                    value={formik.values.shipTo.city}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (sameAsBillTo) setSameAsBillTo(false);
                    }}
                    margin="dense"
                    size="small"
                    disabled={sameAsBillTo}
                  />
                  <TextField
                    fullWidth
                    label="State"
                    name="shipTo.state"
                    value={formik.values.shipTo.state}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (sameAsBillTo) setSameAsBillTo(false);
                    }}
                    margin="dense"
                    size="small"
                    disabled={sameAsBillTo}
                  />
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="shipTo.pincode"
                    value={formik.values.shipTo.pincode}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (sameAsBillTo) setSameAsBillTo(false);
                    }}
                    margin="dense"
                    size="small"
                    disabled={sameAsBillTo}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Items Table */}
          <Typography variant="h6" gutterBottom>
            Items
          </Typography>
          <FieldArray name="items">
            {({ push, remove }) => (
              <Box sx={{ mb: 3 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                        <TableCell sx={{ width: '5%' }}><strong>S.No</strong></TableCell>
                        <TableCell sx={{ width: '25%' }}><strong>Description</strong></TableCell>
                        <TableCell sx={{ width: '10%' }}><strong>HSN</strong></TableCell>
                        <TableCell sx={{ width: '8%' }}><strong>Qty</strong></TableCell>
                        <TableCell sx={{ width: '8%' }}><strong>Unit</strong></TableCell>
                        <TableCell sx={{ width: '10%' }}><strong>Rate</strong></TableCell>
                        <TableCell sx={{ width: '8%' }}><strong>CGST%</strong></TableCell>
                        <TableCell sx={{ width: '8%' }}><strong>SGST%</strong></TableCell>
                        <TableCell sx={{ width: '12%' }} align="right"><strong>Amount</strong></TableCell>
                        <TableCell sx={{ width: '6%' }} align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formik.values.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              fullWidth
                              options={descriptionOptions}
                              value={item.description}
                              onChange={(_event, newValue) => {
                                formik.setFieldValue(`items.${index}.description`, newValue || '');
                              }}
                              onInputChange={(_event, newInputValue) => {
                                formik.setFieldValue(`items.${index}.description`, newInputValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  name={`items.${index}.description`}
                                  size="small"
                                  placeholder="Enter or select description"
                                  variant="standard"
                                  error={
                                    formik.touched.items?.[index]?.description &&
                                    Boolean((formik.errors.items as any)?.[index]?.description)
                                  }
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.hsn`}
                              value={item.hsn}
                              onChange={formik.handleChange}
                              size="small"
                              placeholder="HSN"
                              variant="standard"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.qty`}
                              type="number"
                              value={item.qty}
                              onChange={formik.handleChange}
                              size="small"
                              variant="standard"
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.unit`}
                              value={item.unit}
                              onChange={formik.handleChange}
                              size="small"
                              variant="standard"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.rate`}
                              type="number"
                              value={item.rate}
                              onChange={formik.handleChange}
                              size="small"
                              variant="standard"
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.cgstPercent`}
                              type="number"
                              value={item.cgstPercent}
                              onChange={formik.handleChange}
                              size="small"
                              variant="standard"
                              inputProps={{ min: 0, max: 100, step: 0.1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              name={`items.${index}.sgstPercent`}
                              type="number"
                              value={item.sgstPercent}
                              onChange={formik.handleChange}
                              size="small"
                              variant="standard"
                              inputProps={{ min: 0, max: 100, step: 0.1 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              ₹{item.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => remove(index)}
                              size="small"
                              disabled={formik.values.items.length === 1}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    push({
                      sno: formik.values.items.length + 1,
                      description: '',
                      hsn: '',
                      qty: 1,
                      unit: 'NOS',
                      rate: 0,
                      cgstPercent: 9,
                      cgstAmount: 0,
                      sgstPercent: 9,
                      sgstAmount: 0,
                      amount: 0,
                    })
                  }
                  sx={{ mt: 1 }}
                >
                  Add Item
                </Button>
              </Box>
            )}
          </FieldArray>

          {/* Totals Summary */}
          <Card sx={{ mb: 3, bgcolor: 'primary.lighter' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography>Sub Total:</Typography>
                </Grid>
                <Grid size={{ xs: 6 }} textAlign="right">
                  <Typography fontWeight="bold">₹{formik.values.subTotal.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography>CGST Total:</Typography>
                </Grid>
                <Grid size={{ xs: 6 }} textAlign="right">
                  <Typography fontWeight="bold">₹{formik.values.cgstTotal.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography>SGST Total:</Typography>
                </Grid>
                <Grid size={{ xs: 6 }} textAlign="right">
                  <Typography fontWeight="bold">₹{formik.values.sgstTotal.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="h6">Grand Total:</Typography>
                </Grid>
                <Grid size={{ xs: 6 }} textAlign="right">
                  <Typography variant="h6" color="primary">₹{formik.values.grandTotal.toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" fontStyle="italic">
                    {formik.values.totalInWords}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {onCancel && (
              <Button onClick={onCancel} variant="outlined">
                Cancel
              </Button>
            )}
            <Button
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              variant="outlined"
            >
              Preview
            </Button>
            <Button
              startIcon={<PdfIcon />}
              onClick={handleDownload}
              variant="outlined"
            >
              Download PDF
            </Button>
            {onSave && (
              <Button
                onClick={handleSaveClick}
                startIcon={<SaveIcon />}
                variant="contained"
              >
                Save Invoice
              </Button>
            )}
          </Box>
        </Box>
      </form>

      {/* Error Snackbar */}
      <Snackbar
        open={errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Invoice Preview</DialogTitle>
        <DialogContent>
          {pdfUrl && (
            <Box
              sx={{
                width: '100%',
                height: '80vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <iframe
                src={pdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title="Invoice Preview"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={() => {
              handleDownload();
              handleClosePreview();
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </FormikProvider>
  );
};

export default EditableInvoiceForm;
